import React, { useState, useEffect } from 'react';
import axios from 'axios';
import StatusBadge from './components/StatusBadge';
import { UI_CONSTANTS } from './constants';

interface TaxGroupRowProps {
  stepNumber: number;
}

interface Tax {
  id: number;
  label: string;
  list_rate: number;
  qbol_id?: string;
}

interface TaxGroupData {
  id: number;
  label: string;
  qbol_id?: string;
  qbol_sync?: boolean;
  qbol_message?: string;
  qbol_synced_at?: string;
  taxes: Tax[];
}

const TaxGroupRow: React.FC<TaxGroupRowProps> = ({ stepNumber }) => {
  const [loading, setLoading] = useState(false);
  const [searchId, setSearchId] = useState('');
  const [taxGroupData, setTaxGroupData] = useState<TaxGroupData | null>(null);
  const [qboData, setQboData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cachedItems, setCachedItems] = useState<TaxGroupData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-load latest on mount
  useEffect(() => {
    handleRefresh();
  }, []);

  // Auto QBO lookup when taxGroupData changes
  useEffect(() => {
    if (taxGroupData?.qbol_id) {
      handleQboLookup(taxGroupData.qbol_id);
    } else {
      setQboData(null);
    }
  }, [taxGroupData]);

  const handleQboLookup = async (qboId: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/dev/qbo-taxcode/${qboId}`
      );
      
      if (response.data.success) {
        setQboData(response.data.data);
      } else {
        setQboData(null);
      }
    } catch (err: any) {
      console.error('QBO tax code lookup failed:', err);
      setQboData(null);
    }
  };

  const handleLookup = async () => {
    if (!searchId.trim()) {
      await handleRefresh();
    } else {
      // Fetch specific tax group by ID
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/dev/tax-groups/${searchId}`
        );
        
        if (response.data.success) {
          setTaxGroupData(response.data.data);
          setCachedItems([]);
          setCurrentIndex(0);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message);
        setTaxGroupData(null);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/quickbooks-online/reconciliation/dev/tax-groups?limit=10`
      );
      
      if (response.data.success && response.data.data.length > 0) {
        setCachedItems(response.data.data);
        const first = response.data.data[0];
        setTaxGroupData(first);
        setSearchId('');
        setCurrentIndex(1);
      } else {
        setError('No tax groups found');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (cachedItems.length > 0) {
      let nextIndex = currentIndex;
      if (nextIndex >= cachedItems.length) {
        nextIndex = 0;
      }
      
      const item = cachedItems[nextIndex];
      setTaxGroupData(item);
      setSearchId('');
      setCurrentIndex(nextIndex + 1);
    }
  };

  const handleBack = () => {
    if (cachedItems.length > 0 && currentIndex > 0) {
      const prevIndex = currentIndex - 2;
      const item = cachedItems[prevIndex >= 0 ? prevIndex : cachedItems.length - 1];
      setTaxGroupData(item);
      setSearchId('');
      setCurrentIndex(prevIndex >= 0 ? prevIndex + 1 : cachedItems.length);
    }
  };

  const hasInputValue = searchId.trim().length > 0;
  const showBackButton = cachedItems.length > 0 && currentIndex > 0 && !hasInputValue;
  const status = loading ? 'loading' : error ? 'error' : taxGroupData ? (qboData ? 'both' : 'eserv-only') : 'ready';
  const statusText = loading ? 'Loading...' : error ? 'Error' : taxGroupData ? (qboData ? 'Both' : 'EServ Only') : 'Ready';

  return (
    <tr>
      <td className="border border-gray-300 px-3 py-2 font-medium">{stepNumber}. tax group</td>
      <td className="border border-gray-300 px-3 py-2">
        <div className="flex gap-2 items-center">
          <input
            type="text"
            placeholder="Tax Group ID (empty = latest)"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            className={`px-3 py-1 border border-gray-300 rounded text-sm ${UI_CONSTANTS.INPUT_WIDTH}`}
          />
          <div className="flex flex-col gap-1 items-end">
            {/* Back button */}
            {showBackButton && (
              <button
                onClick={handleBack}
                className="px-2 py-0.5 border border-purple-600 text-purple-600 text-xs rounded disabled:opacity-50 flex items-center gap-0.5 hover:bg-purple-50"
                disabled={loading}
                title="Previous tax group"
              >
                Back
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
            )}
            
            {/* Lookup/Refresh button */}
            <button
              onClick={handleLookup}
              className="px-2 py-0.5 border border-gray-600 text-gray-600 text-xs rounded disabled:opacity-50 flex items-center gap-0.5 hover:bg-gray-50"
              disabled={loading}
              title={hasInputValue ? "Lookup by ID" : "Refresh to max ID"}
            >
              {loading ? '...' : (
                <>
                  {hasInputValue ? 'Lookup' : 'Refresh'}
                  {hasInputValue ? (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                </>
              )}
            </button>
            
            {/* Next button */}
            <button
              onClick={handleNext}
              className="px-2 py-0.5 border border-purple-600 text-purple-600 text-xs rounded disabled:opacity-50 flex items-center gap-0.5 hover:bg-purple-50"
              disabled={loading}
              title="Next tax group"
            >
              {loading ? '...' : (
                <>
                  Next
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      </td>
      <td className={`border border-gray-300 px-3 py-2 ${UI_CONSTANTS.MAX_DATA_WIDTH}`}>
        {taxGroupData && (
          <div className="font-mono text-xs">
            {error ? (
              <div className="text-red-600 bg-red-50 p-2 rounded">
                <strong>Error:</strong> {error}
              </div>
            ) : (
              <div className="bg-gray-50 p-2 rounded border">
                <div>ID: {taxGroupData.id}</div>
                <div>Label: {taxGroupData.label}</div>
                <div>QBO ID: {taxGroupData.qbol_id || 'N/A'}</div>
                <div>Synced: {taxGroupData.qbol_sync ? 'Yes' : 'No'}</div>
                {taxGroupData.taxes && taxGroupData.taxes.length > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    <div className="text-xs font-semibold mb-1">Taxes ({taxGroupData.taxes.length}):</div>
                    {taxGroupData.taxes.map(tax => (
                      <div key={tax.id} className="text-xs pl-2">
                        • {tax.label} ({tax.list_rate}%)
                        {tax.qbol_id && ` - QBO: ${tax.qbol_id}`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </td>
      <td className={`border border-gray-300 px-3 py-2 ${UI_CONSTANTS.MAX_DATA_WIDTH}`}>
        {qboData ? (
          <div className="font-mono text-xs">
            <div className="bg-green-50 p-2 rounded border">
              <div>ID: {qboData.id || 'N/A'}</div>
              <div>Name: {qboData.name || 'N/A'}</div>
              <div>Active: {qboData.active ? 'Yes' : 'No'}</div>
              <div>Taxable: {qboData.taxable ? 'Yes' : 'No'}</div>
              {qboData.salesTaxRateList?.TaxRateDetail && (
                <div className="mt-2 pt-2 border-t">
                  <div className="text-xs font-semibold mb-1">Tax Rates ({qboData.salesTaxRateList.TaxRateDetail.length}):</div>
                  {qboData.salesTaxRateList.TaxRateDetail.map((rate: any, idx: number) => (
                    <div key={idx} className="text-xs pl-2">
                      • {rate.TaxRateName} ({rate.RateValue}%)
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : taxGroupData && !error ? (
          <div className="font-mono text-xs">
            <div className="bg-red-50 p-2 rounded border border-red-200">
              <div className="text-red-700 mb-2">
                <div className="flex items-center gap-1 mb-1">
                  <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span className="text-xs font-medium">QBO Data Missing</span>
                </div>
                <div className="text-xs text-red-600">
                  Tax group not found in QuickBooks
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="font-mono text-xs text-gray-500">N/A</div>
        )}
      </td>
      <td className="border border-gray-300 px-3 py-2">
        <div className="font-mono text-xs text-gray-600">
          {loading ? (
            <div className="text-gray-500">Searching...</div>
          ) : error ? (
            <div className="text-red-600">
              <div>Error occurred</div>
              <div>Check EServ column</div>
            </div>
          ) : taxGroupData ? (
            <>
              <div>Found: tax group</div>
              <div>ID: {taxGroupData.id}</div>
              <div>Label: {taxGroupData.label}</div>
              <div>QBO: {taxGroupData.qbol_id ? 'Synced' : 'Not Synced'}</div>
            </>
          ) : (
            <div className="text-gray-500">Ready to search</div>
          )}
        </div>
      </td>
      <td className="border border-gray-300 px-2 py-2 text-center">
        <StatusBadge status={status} text={statusText} />
      </td>
      <td className="border border-gray-300 px-3 py-2">
        <div className="text-gray-500 text-xs">-</div>
      </td>
      <td className="border border-gray-300 px-3 py-2">
        <div className="text-gray-500 text-xs">-</div>
      </td>
    </tr>
  );
};

export default TaxGroupRow;

